import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, ButtonBase, CardContent, ClickAwayListener, Grid, IconButton, Paper, Popper, Stack, Tab, Tabs, Typography } from '@mui/material';
// project import
import MainCard from 'components/MainCard';
// import Transitions from 'components/@extended/Transitions';
import ProfileTab from './ProfileTab';
import SettingTab from './SettingTab';

// assets
import { CaretDownOutlined, LogoutOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useGetIndividualDataMutation } from 'store/features/profile/profileApi';
// import useSocket from 'hooks/Socket/useSocket';
import { userLoggedOut } from 'store/features/auth/authSlice';
import { resetMenu } from 'store/features/menu/menuSlice';
import { resetProcess } from 'store/features/process/processSlice';
import { resetProfile } from 'store/features/profile/profileSlice';
import { resetMain } from 'store/reducer/mainSlice';
import { socket } from 'utils/socket';
import { UserAvatar3 } from 'sections/chat/UserAvatar';
import { apiSlice } from 'store/features/api/apiSlice';
import { persistor } from 'store/store';

// tab panel wrapper
function TabPanel({ children, value, index, ...other }) {
	return (
		<div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
			{value === index && children}
		</div>
	);
}

TabPanel.propTypes = {
	children: PropTypes.node,
	index: PropTypes.any.isRequired,
	value: PropTypes.any.isRequired,
};

function a11yProps(index) {
	return {
		id: `profile-tab-${index}`,
		'aria-controls': `profile-tabpanel-${index}`,
	};
}

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

const Profile = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const token = useSelector((state) => state.auth.accessToken);
	const isAuthorized = useSelector((state) => state.auth.userId);
	const profileData = useSelector((state) => state.profile.profileData);
	const isProfileUpdate = useSelector((state) => state.auth.isProfileUpdate);

	const [userInfo, setUserInfo] = useState(null);

	const [GetIndividualData, { data: getIndividualData }] = useGetIndividualDataMutation();
	// const { DisconnectFromSocket } = useSocket();

	useEffect(() => {
		if (isAuthorized) {
			GetIndividualData({});
		}
	}, [isAuthorized, isProfileUpdate]);

	useEffect(() => {
		if (getIndividualData?.[0]) {
			setUserInfo(getIndividualData[0]);
		}
	}, [getIndividualData]);

	const handleLogout = () => {
		socket.emit('user_out', { accessToken: token });
		socket.disconnect();

		localStorage.clear();

		dispatch(userLoggedOut());
		dispatch(resetMenu());
		dispatch(resetProcess());
		dispatch(resetProfile());
		dispatch(resetMain());
		dispatch(apiSlice.util.resetApiState());
		localStorage.setItem('lyn:logout', Date.now().toString());
		persistor.purge();
		navigate('/login');
	};

	const anchorRef = useRef(null);
	const [open, setOpen] = useState(false);
	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleClose = (event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target)) {
			return;
		}
		setOpen(false);
	};

	const [value, setValue] = useState(0);

	const handleChange = (event, newValue) => {
		setValue(newValue);
	};

	return (
		<Box sx={{ flexShrink: 0, ml: 1.5 }}>
			<ButtonBase
				sx={{
					p: 0.25,
					bgcolor: open ? 'grey.300' : 'transparent',
					borderRadius: 1,
					'&:hover': { bgcolor: 'secondary.lighter' },
				}}
				aria-label="open profile"
				ref={anchorRef}
				aria-controls={open ? 'profile-grow' : undefined}
				aria-haspopup="true"
				onClick={handleToggle}
			>
				<Stack direction="row" spacing={0.5} alignItems="center" sx={{ p: 0.5 }}>
					<UserAvatar3 userId={userInfo?.entityCode} width={30} height={30} />
					<CaretDownOutlined />
				</Stack>
			</ButtonBase>
			<Popper
				placement="bottom-end"
				open={open}
				anchorEl={anchorRef.current}
				role={undefined}
				disablePortal
				popperOptions={{
					modifiers: [
						{
							name: 'offset',
							options: {
								offset: [0, 9],
							},
						},
					],
				}}
			>
				<ClickAwayListener onClickAway={handleClose}>
					<Paper
						sx={{
							boxShadow: theme.customShadows.z1,
							width: 290,
							minWidth: 240,
							maxWidth: 290,
							[theme.breakpoints.down('md')]: {
								maxWidth: 250,
							},
						}}
					>
						<MainCard elevation={0} border={false} content={false}>
							<CardContent sx={{ px: 2.5, pt: 3 }}>
								<Grid container justifyContent="space-between" alignItems="center" wrap="nowrap">
									<Grid item>
										<Stack direction="row" spacing={1.25} alignItems="center">
											<UserAvatar3
												userId={userInfo?.entityCode}
												companyCode={profileData?.creatorOrganizationId}
												width={30}
												height={30}
											/>
											<Stack>
												<Typography variant="h6">{userInfo?.entityFullName}</Typography>
												<Typography variant="body2" color="textSecondary">
													{localStorage?.getItem('companyName')}
												</Typography>
											</Stack>
										</Stack>
									</Grid>

									<Grid item>
										<IconButton
											size="large"
											sx={{ p: 0, width: '24px', height: '24px' }}
											color="secondary"
											onClick={handleLogout}
										>
											<LogoutOutlined />
										</IconButton>
									</Grid>
								</Grid>
							</CardContent>
							<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
								<Tabs variant="fullWidth" value={value} onChange={handleChange} aria-label="profile tabs">
									<Tab
										sx={{
											display: 'flex',
											flexDirection: 'row',
											justifyContent: 'center',
											alignItems: 'center',
											textTransform: 'capitalize',
										}}
										icon={<UserOutlined style={{ marginBottom: 0, marginRight: '10px' }} />}
										label="Profile"
										{...a11yProps(0)}
									/>
									<Tab
										sx={{
											display: 'flex',
											flexDirection: 'row',
											justifyContent: 'center',
											alignItems: 'center',
											textTransform: 'capitalize',
										}}
										icon={<SettingOutlined style={{ marginBottom: 0, marginRight: '10px' }} />}
										label="Setting"
										{...a11yProps(1)}
									/>
								</Tabs>
							</Box>
							<TabPanel value={value} index={0} dir={theme.direction}>
								<ProfileTab setOpen={setOpen} userInfo={userInfo} handleLogout={handleLogout} />
							</TabPanel>
							<TabPanel value={value} index={1} dir={theme.direction}>
								<SettingTab />
							</TabPanel>
						</MainCard>
					</Paper>
				</ClickAwayListener>
			</Popper>
		</Box>
	);
};

export default Profile;
